import {
  faLightbulb,
  faUpRightFromSquare,
  faWarning,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Breadcrumbs,
  Button,
  Content,
  DetailList,
  Heading,
  Loading,
  Popover,
  PopoverContent,
} from '@orobix/orobix-fe-components'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'

import { PredictedExpectedSelection } from '../../components/ConfusionMatrix/ChartConfusionMatrix'
import ConfusionMatrix from '../../components/ConfusionMatrix/ConfusionMatrix'
import PageHeader from '../../components/PageHeader/PageHeader'
import { useDispatch, useStore } from '../../store/store'
import {
  ConfidenceMetric,
  TrainingTestOutcomesParams,
} from '../../store/store.types'
import {
  fetchDatasetsUsedInTrainingTest,
  fetchProduct,
  fetchProject,
  fetchTraining,
  fetchTrainingTest,
} from '../../store/storeActions'
import { BRUSH_ANNOTATION_TYPE } from '../../types/annotation'
import { formatDate } from '../../utils/date'
import { overrideLabelStudioSettings } from '../../utils/labelStudioHelper'
import { locations } from '../../utils/locations'
import { getOverlappingScoresMetric } from '../../utils/metric'
import { getQueryString } from '../../utils/queryString'
import { getOverallPerformanceName, toScore } from '../../utils/trainings'
import { AnomalyTestOutcomesList } from './AnomalyTestOutcomesList'
import { ClassificationTestOutcomesList } from './ClassificationTestOutcomesList'
import { ScoresHistogram } from './ScoresHistogram'
import TrainingTestObservations from './TrainingTestObservations'
import useStyles from './TrainingTestRoot.styles'
import { useFetchAllTestOutcomes } from './useFetchAllTestOutcomes'

interface RouteParams {
  projectId: string
  productId: string
  trainingId: string
  trainingTestId: string
}

const TrainingTestRoot = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { product, project, training, trainingTest } = useStore()
  const dispatch = useDispatch()
  const location = useLocation()

  const {
    productId,
    projectId,
    trainingId,
    trainingTestId,
  } = useParams<RouteParams>()

  function getDataOfSchemaType(schematype: string, responseAsJSONObject: any) {
    let d = []
    if (
      undefined === responseAsJSONObject ||
      (responseAsJSONObject as Array<string>).length === 0
    ) {
      return []
    }

    const v = responseAsJSONObject.filter(
      (o: any) => o.schemaType === schematype
    )
    if (undefined === v || undefined === v[0]) {
      return []
    }
    d = v[0].data
    return d
  }

  const [
    trainingTestOutcomesParams,
    setTrainingTestOutcomesParams,
  ] = useState<TrainingTestOutcomesParams>({})

  const [datasetsUsedInTest, setDatasetsUsedInTest] = useState<
    Array<{ id: number; name: string; updateDate: string }>
  >([])

  const { testOutcomes } = useFetchAllTestOutcomes(
    Number(trainingTestId),
    trainingTestOutcomesParams
  )

  const data =
    testOutcomes && getDataOfSchemaType('confusion_matrix', testOutcomes)
  const classLabels = data && data.classLabels
  const matrix = data && data.matrix

  const confusionMatrixData = trainingTest &&
    data &&
    matrix &&
    classLabels && {
      matrix,
      classLabels,
    }

  const history = useHistory()

  useEffect(() => {
    const params = getQueryString(trainingTestOutcomesParams)
    history.push(
      locations.trainingTestSimulation(
        Number(projectId),
        Number(productId),
        Number(trainingId),
        Number(trainingTestId),
        params
      )
    )
  }, [
    history,
    projectId,
    productId,
    trainingId,
    trainingTestId,
    trainingTestOutcomesParams,
  ])

  const onPredictedExpectedClicked = useCallback(
    (predictedExpectedSelection: PredictedExpectedSelection) => {
      setTrainingTestOutcomesParams({
        ...trainingTestOutcomesParams,
        prediction: predictedExpectedSelection.prediction,
        expectation: predictedExpectedSelection.expectation,
      })
    },
    [trainingTestOutcomesParams]
  )

  const onThresholdChanged = useCallback(
    (newThreshold: number, simulationMode: string) => {
      setTrainingTestOutcomesParams({
        ...trainingTestOutcomesParams,
        threshold: `${newThreshold}`,
        simulationMode,
      })
    },
    [trainingTestOutcomesParams]
  )

  const overlappingScores =
    trainingTest &&
    trainingTest.metrics &&
    getOverlappingScoresMetric(trainingTest.metrics)

  const threshold =
    (trainingTest &&
      getDataOfSchemaType('anomaly_score_histogram', testOutcomes).threshold) ||
    0

  const anomalyScores =
    (testOutcomes &&
      getDataOfSchemaType('anomaly_score_histogram', testOutcomes)
        .anomalyScores) ||
    []

  const normalScores =
    (testOutcomes &&
      getDataOfSchemaType('anomaly_score_histogram', testOutcomes)
        .normalScores) ||
    []

  const showHistogram =
    threshold && (anomalyScores.length !== 0 || normalScores.length !== 0)

  const confidence =
    trainingTest &&
    (trainingTest.metrics.filter(
      (m) => m.schemaType === 'confidence'
    )[0] as ConfidenceMetric)?.data

  useEffect(() => {
    if (!project) {
      fetchProject(dispatch, Number(projectId))
    }
  }, [dispatch, project, projectId, location.key])

  useEffect(() => {
    if (!product) {
      fetchProduct(dispatch, Number(productId))
    }
  }, [dispatch, product, productId, location.key])

  useEffect(() => {
    if (!training) {
      fetchTraining(dispatch, Number(trainingId))
    }
  }, [dispatch, training, trainingId, location.key])

  useEffect(() => {
    if (!trainingTest) {
      fetchTrainingTest(dispatch, Number(trainingTestId))
    }
  }, [dispatch, trainingTest, trainingTestId, location.key])

  useEffect(() => {
    if (product && product.productType)
      overrideLabelStudioSettings(product.productType.name)
  }, [product])

  useEffect(() => {
    if (trainingTestId) {
      fetchDatasetsUsedInTrainingTest(Number(trainingTestId))
        .then((datasets) => {
          setDatasetsUsedInTest(datasets)
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.error('Error fetching datasets used in training test')
        })
    }
  }, [trainingTestId])

  return product && project && training && trainingTest ? (
    <Content
      childrenClassName={classes.content}
      breadcrumbs={
        <Breadcrumbs
          elements={[
            <Link to={locations.projects}>{t('Projects')}</Link>,
            <Link to={locations.products(Number(projectId))}>
              {project.name}
            </Link>,
            <Link
              to={locations.trainings(Number(projectId), Number(productId))}
            >
              {product.name}
            </Link>,
            <Link
              to={locations.trainings(
                Number(projectId),
                Number(productId),
                Number(trainingId)
              )}
            >
              {training.name}
            </Link>,
            trainingTest.name,
          ]}
        />
      }
    >
      <div className={classes.trainingTest}>
        <div className={classes.list}>
          <PageHeader title={trainingTest.name} />
          {
            {
              classification: (
                <ClassificationTestOutcomesList
                  trainingTestId={Number(trainingTestId)}
                  trainingTestOutcomesParams={trainingTestOutcomesParams}
                  setTrainingTestOutcomesParams={setTrainingTestOutcomesParams}
                />
              ),
              segmentation: (
                <TrainingTestObservations
                  annotatorTypeGT={BRUSH_ANNOTATION_TYPE}
                  annotatorTypeNN={BRUSH_ANNOTATION_TYPE}
                />
              ),
              classification_patch: (
                <TrainingTestObservations
                  annotatorTypeGT={BRUSH_ANNOTATION_TYPE}
                  annotatorTypeNN={BRUSH_ANNOTATION_TYPE}
                />
              ),
              anomaly: (
                <AnomalyTestOutcomesList
                  trainingTestId={Number(trainingTestId)}
                  trainingTestOutcomesParams={trainingTestOutcomesParams}
                  setTrainingTestOutcomesParams={setTrainingTestOutcomesParams}
                />
              ),
            }[product.type]
          }
        </div>

        <div className={classes.sidePanel}>
          <Heading headingLevel="2" noTopMargin>
            {t('Test summary')}
          </Heading>

          <DetailList
            titleColumnWidth="6rem"
            list={[
              {
                title: t('Id'),
                value: trainingTest.id,
              },
              {
                title: t('Created on'),
                value: formatDate(trainingTest.creationDate),
              },
              ...(confidence
                ? [
                    {
                      title: t('Confidence'),
                      value: toScore(confidence),
                    },
                  ]
                : []),
              ...(trainingTest.metadata && trainingTest.metadata.threshold
                ? [
                    {
                      title: t('Optimal threshold'),
                      value: trainingTest.metadata.threshold,
                    },
                  ]
                : []),
              ...(overlappingScores
                ? [
                    {
                      title: t('Overlapping scores'),
                      value: overlappingScores.data,
                    },
                  ]
                : []),
              ...(trainingTest.notes
                ? [{ title: t('Notes'), value: trainingTest.notes }]
                : []),
            ]}
          />

          {datasetsUsedInTest.length > 0 &&
            trainingTest.name !== 'automatic-test' && (
              <>
                <Heading headingLevel="3" headingLevelStyle="4" noBottomMargin>
                  {t('Datasets' as any)}
                </Heading>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.th}>{t('Name')}</th>
                      <th className={classes.th}>{t('Updated on')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datasetsUsedInTest.map((dataset) => (
                      <tr key={dataset.id}>
                        <td className={classes.td}>{dataset.name}</td>
                        <td className={classes.td}>
                          {dataset.updateDate
                            ? `${format(new Date(dataset.updateDate), 'PP', {
                                locale: enUS,
                              })} ${format(new Date(dataset.updateDate), 'p', {
                                locale: enUS,
                              })}`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

          {!!trainingTest.overallPerformance && !!confusionMatrixData && (
            <ConfusionMatrix
              withDetails
              globalResultTitle={getOverallPerformanceName(
                trainingTest.overallPerformance.schemaType
              )}
              globalResultValue={toScore(
                getDataOfSchemaType(
                  trainingTest.overallPerformance.schemaType,
                  testOutcomes
                ).value || 0
              )}
              data={confusionMatrixData}
              samplesTypeTitle={
                {
                  classification: t('Images'),
                  classification_patch: t('Patches'),
                  segmentation: t('Images'),
                  anomaly: t('Images'),
                }[product.type]
              }
              product={product}
              falseBadRate={toScore(
                getDataOfSchemaType('false_bad_rate', testOutcomes) || 0
              )}
              falseGoodRate={toScore(
                getDataOfSchemaType('false_good_rate', testOutcomes) || 0
              )}
              onPredictedExpectedClicked={onPredictedExpectedClicked}
              prediction={trainingTestOutcomesParams.prediction}
            />
          )}
          {!!showHistogram && (
            <div className={classes.chartContainer}>
              <div className={classes.histogramHeader}>
                <p className={classes.chartHeader}>
                  {t('Cumulative plot of anomaly score')}
                </p>
                <Popover
                  renderContent={() => (
                    <PopoverContent width="max-content">
                      <ul
                        className={classes.paddingLeft} // fix user agent stylesheet
                      >
                        <li>{t('Green line: normal')}</li>
                        <li>{t('Red line: anomaly')}</li>
                        <li>{t('Dotted line: threshold')}</li>
                      </ul>
                    </PopoverContent>
                  )}
                >
                  <Button
                    label={t('Tips')}
                    title={t('Tips')}
                    hideLabel
                    icon={<FontAwesomeIcon icon={faLightbulb} />}
                    type="blank-default"
                    buttonType="button"
                  />
                </Popover>
                <Link
                  to={locations.anomalyScorePlot(
                    Number(projectId),
                    Number(productId),
                    Number(trainingId),
                    Number(trainingTestId)
                  )}
                  target="_blank"
                >
                  <FontAwesomeIcon
                    className={classes.indented}
                    title={t('Open zoomed plot in a new tab')}
                    icon={faUpRightFromSquare}
                  />
                </Link>
              </div>
              <ScoresHistogram
                anomalyScores={anomalyScores}
                normalScores={normalScores}
                thresholdX={threshold}
                onThresholdChanged={onThresholdChanged}
              />
              {anomalyScores.length + normalScores.length > 200 && (
                <Link
                  to={locations.anomalyScorePlot(
                    Number(projectId),
                    Number(productId),
                    Number(trainingId),
                    Number(trainingTestId)
                  )}
                  target="_blank"
                >
                  <FontAwesomeIcon icon={faWarning} />
                  {t(
                    'Many bins to show, click to open zoomed plot in a new tab'
                  )}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </Content>
  ) : (
    <Content>
      <Loading />
    </Content>
  )
}

export default TrainingTestRoot
